import React, { useState } from 'react';
import { Image, Palette, ScanSearch } from 'lucide-react';
import ImageAdjustModal from '../ImageAdjustModal';
import { copyToClipboard } from '../../utils/clipboard';

const HouseholdSection = ({ hasHouse, household, handleCreate, handleJoin, handleResetPin, onUpdate, onImageUpload }) => {
    const [newHouseholdName, setNewHouseholdName] = useState('');
    const [newHouseholdCode, setNewHouseholdCode] = useState('');
    const [inviteCode, setInviteCode] = useState('');
    const [joinCode, setJoinCode] = useState('');
    const [isResetting, setIsResetting] = useState(false);
    const [newPin, setNewPin] = useState("");
    const [copied, setCopied] = useState(false);

    const [isEditing, setIsEditing] = useState(false);
    const [editName, setEditName] = useState(household?.name || '');
    const [editDesc, setEditDesc] = useState(household?.description || '');
    const [editBanner, setEditBanner] = useState(household?.bannerUrl || '');
    const [editBannerPosition, setEditBannerPosition] = useState(household?.bannerPosition || '50% 50%');
    const [editBannerZoom, setEditBannerZoom] = useState(household?.bannerZoom || 1);
    const [uploading, setUploading] = useState(false);

    // State for the adjust modal
    const [adjustModalUrl, setAdjustModalUrl] = useState(null);

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        try {
            const uploadedUrl = await onImageUpload(file);
            // Open the adjust modal immediately after upload
            setAdjustModalUrl(uploadedUrl);
        } catch (err) {
            alert(err.response?.data?.error || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    // Called when user saves positioning in the banner adjust modal
    const handleAdjustSave = (position, zoom) => {
        setEditBanner(adjustModalUrl);
        setEditBannerPosition(position);
        setEditBannerZoom(zoom);
        setAdjustModalUrl(null);
    };

    const handleSave = () => {
        onUpdate({ name: editName, description: editDesc, bannerUrl: editBanner, bannerPosition: editBannerPosition, bannerZoom: editBannerZoom });
        setIsEditing(false);
    };

    if (hasHouse) {
        return (
            <div className="bg-ink-card rounded-2xl shadow-sm border border-white/10 overflow-hidden">
                <div className="relative h-24 bg-ink-card3 border-b border-white/10 overflow-hidden">
                    {household?.bannerUrl && (
                        <img
                            src={household.bannerUrl}
                            alt="Banner"
                            className="absolute inset-0 w-full h-full object-cover opacity-70"
                            style={{
                                objectPosition: household?.bannerPosition || '50% 50%',
                                transform: `scale(${household?.bannerZoom || 1})`,
                                transformOrigin: household?.bannerPosition || '50% 50%',
                            }}
                            onError={(e) => e.target.style.display = 'none'}
                        />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-ink-overlay/90 via-ink-overlay/20 to-transparent flex flex-col justify-end p-5">
                        <div className="flex justify-between items-end">
                            <div>
                                <h3 className="text-[10px] font-mono font-medium text-mint uppercase tracking-widest mb-0.5">Household</h3>
                                <h2 className="text-xl font-serif text-ink-banner">{household?.name}</h2>
                            </div>
                            {!isEditing && (
                                <button onClick={() => setIsEditing(true)} className="p-2 bg-ink-card/80 backdrop-blur rounded-full shadow-sm hover:bg-ink-card transition-colors border border-white/15">
                                    <Palette size={14} className="text-ink-fg/70" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <div className="p-5 space-y-6">
                    {isEditing ? (
                        <div className="space-y-4 bg-ink-card3 p-4 rounded-2xl border border-white/10">
                            <div>
                                <label className="block text-[10px] font-mono font-medium text-ink-fg/40 uppercase mb-1">House Name</label>
                                <input value={editName} onChange={e => setEditName(e.target.value)} className="w-full bg-ink-card2 border border-white/10 text-ink-fg rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-mint" />
                            </div>
                            <div>
                                <label className="block text-[10px] font-mono font-medium text-ink-fg/40 uppercase mb-1">Motto / Description</label>
                                <textarea value={editDesc} onChange={e => setEditDesc(e.target.value)} className="w-full bg-ink-card2 border border-white/10 text-ink-fg rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-mint" rows="2" />
                            </div>
                            <div>
                                <label className="block text-[10px] font-mono font-medium text-ink-fg/40 uppercase mb-2">Banner Photo</label>

                                {/* Preview + Adjust button if banner is set */}
                                {editBanner && (
                                    <div className="relative h-20 rounded-xl overflow-hidden mb-3 border border-white/10 bg-ink-card2 group cursor-pointer" onClick={() => setAdjustModalUrl(editBanner)}>
                                        <img
                                            src={editBanner}
                                            alt="Banner preview"
                                            className="w-full h-full object-cover"
                                            style={{
                                                objectPosition: editBannerPosition,
                                                transform: `scale(${editBannerZoom})`,
                                                transformOrigin: editBannerPosition,
                                            }}
                                        />
                                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center">
                                            <div className="flex items-center gap-1.5 px-3 py-2 bg-ink-card/90 text-ink-fg text-xs font-bold rounded-lg opacity-0 group-hover:opacity-100 transition-opacity shadow-sm">
                                                <ScanSearch size={14} /> Adjust crop
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="flex gap-2">
                                    <input value={editBanner} onChange={e => setEditBanner(e.target.value)} placeholder="https://..." className="flex-1 bg-ink-card2 border border-white/10 text-ink-fg rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-mint placeholder:text-ink-fg/30" />
                                    <label className={`cursor-pointer flex items-center justify-center p-2 rounded-xl border border-white/10 bg-ink-card2 hover:bg-ink-card3 transition-colors ${uploading ? 'animate-pulse opacity-50' : ''}`}>
                                        <Image size={18} className="text-ink-fg/60" />
                                        <input type="file" className="hidden" accept="image/*" onChange={handleFileChange} disabled={uploading} />
                                    </label>
                                </div>
                                <p className="text-[10px] text-ink-fg/40 mt-1 italic">Upload or paste URL • tap the preview to adjust crop</p>
                            </div>
                            <div className="flex gap-2">
                                <button onClick={handleSave} className="flex-1 bg-mint text-ink-shell font-bold py-2 rounded-xl text-sm shadow-sm hover:brightness-95">Save</button>
                                <button onClick={() => setIsEditing(false)} className="flex-1 bg-ink-card2 text-ink-fg/60 font-bold py-2 rounded-xl border border-white/10 text-sm hover:bg-ink-card3">Cancel</button>
                            </div>
                        </div>
                    ) : (
                        household?.description && (
                            <div className="bg-ink-card3 p-4 rounded-xl border border-white/10 text-sm text-ink-fg/60 italic">
                                "{household.description}"
                            </div>
                        )
                    )}

                    <div className="space-y-3">
                        <h3 className="font-semibold text-ink-fg">Invite Roommates</h3>
                        <p className="text-sm text-ink-fg/50">Share this unique code to let others join:</p>
                        <div className="flex items-center bg-ink-card3 p-2 rounded-xl border border-white/10 copy-container">
                            <code className="text-lg font-mono text-mint px-3 flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
                                {household?.inviteCode || "Loading..."}
                            </code>
                            <button
                                onClick={() => copyToClipboard(household?.inviteCode, setCopied)}
                                className={`px-4 py-2 rounded-lg font-semibold transition-all shadow-sm ${copied ? 'bg-mint text-ink-shell' : 'bg-ink-card2 text-ink-fg/70 border border-white/10 hover:bg-ink-card'}`}
                                style={{ minWidth: '110px' }}
                            >
                                {copied ? "Copied!" : "Copy Code"}
                            </button>
                        </div>
                    </div>

                    <div className="pt-4 border-t border-white/10">
                        <h3 className="font-semibold text-ink-fg mb-3">Security</h3>
                        {isResetting ? (
                            <div className="space-y-3">
                                <input
                                    type="text"
                                    placeholder="Enter new PIN"
                                    value={newPin}
                                    onChange={(e) => setNewPin(e.target.value)}
                                    className="w-full bg-ink-card3 border border-white/10 text-ink-fg rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-mint outline-none transition-all placeholder:text-ink-fg/30"
                                />
                                <div className="flex gap-2">
                                    <button onClick={() => handleResetPin(newPin, setIsResetting, setNewPin)} className="flex-1 bg-mint text-ink-shell font-semibold py-2.5 rounded-xl hover:brightness-95 transition-all">Save PIN</button>
                                    <button onClick={() => setIsResetting(false)} className="flex-1 bg-ink-card3 text-ink-fg/60 font-semibold py-2.5 rounded-xl hover:bg-ink-card2 transition-colors">Cancel</button>
                                </div>
                            </div>
                        ) : (
                            <button onClick={() => setIsResetting(true)} className="w-full bg-ink-card3 text-ink-fg/70 font-semibold py-2.5 rounded-xl hover:bg-ink-card2 transition-colors">
                                Reset Household PIN
                            </button>
                        )}
                    </div>

                </div>

                {/* Banner Adjust Modal */}
                {adjustModalUrl && (
                    <ImageAdjustModal
                        imageUrl={adjustModalUrl}
                        initialPosition={editBannerPosition}
                        initialZoom={editBannerZoom}
                        isCircle={false}
                        onSave={handleAdjustSave}
                        onClose={() => setAdjustModalUrl(null)}
                    />
                )}
            </div>
        );
    }

    return (
        <div className="space-y-5">
            {/* JOIN SECTION — primary path */}
            <div className="p-6 bg-mint/10 rounded-2xl shadow-sm border border-mint/25">
                <h3 className="font-serif text-xl mb-1 text-ink-fg">Join a household</h3>
                <p className="text-xs text-ink-fg/50 mb-4">Ask a roommate for the invite code on their Profile tab.</p>
                <input
                    placeholder="Exact Household invite code"
                    onChange={e => setInviteCode(e.target.value)}
                    className="w-full bg-ink-card3 border border-white/10 text-ink-fg rounded-xl px-4 py-3 mb-3 focus:ring-2 focus:ring-mint outline-none transition-all placeholder:text-ink-fg/30"
                />
                <input
                    type="password"
                    placeholder="Enter PIN"
                    onChange={e => setJoinCode(e.target.value)}
                    className="w-full bg-ink-card3 border border-white/10 text-ink-fg rounded-xl px-4 py-3 mb-4 focus:ring-2 focus:ring-mint outline-none transition-all placeholder:text-ink-fg/30"
                />
                <button onClick={() => handleJoin(inviteCode, joinCode)} className="w-full bg-mint hover:brightness-95 text-ink-shell py-3 rounded-xl font-bold shadow-sm transition-all active:scale-95">
                    Join Household
                </button>
            </div>

            <div className="flex items-center gap-3 px-1">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-[10px] font-mono font-medium text-ink-fg/30 uppercase tracking-widest">Or start one</span>
                <div className="flex-1 h-px bg-white/10" />
            </div>

            {/* CREATE SECTION — secondary path */}
            <div className="p-6 bg-ink-card rounded-2xl shadow-sm border border-white/10">
                <h3 className="font-serif text-lg mb-4 text-ink-fg">Create a household</h3>
                <input
                    placeholder="Household Name (e.g. Wayne Manor)"
                    onChange={e => setNewHouseholdName(e.target.value)}
                    className="w-full bg-ink-card2 border border-white/10 text-ink-fg rounded-xl px-4 py-3 mb-3 focus:ring-2 focus:ring-mint outline-none transition-all placeholder:text-ink-fg/30"
                />
                <input
                    type="password"
                    placeholder="Set a Join PIN (e.g. 1234)"
                    onChange={e => setNewHouseholdCode(e.target.value)}
                    className="w-full bg-ink-card2 border border-white/10 text-ink-fg rounded-xl px-4 py-3 mb-4 focus:ring-2 focus:ring-mint outline-none transition-all placeholder:text-ink-fg/30"
                />
                <button onClick={() => handleCreate(newHouseholdName, newHouseholdCode)} className="w-full bg-ink-card2 hover:bg-ink-card3 border border-white/15 text-ink-fg py-3 rounded-xl font-bold transition-all active:scale-95">
                    Create &amp; Join
                </button>
            </div>
        </div>
    );
};

export default HouseholdSection;
